// src/Pages/CoreModules/MarketsTrade.jsx
import { useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import MarketRegistry          from "../../component/MarketTrad/Marketregistry";
import MarketPrices            from "../../component/MarketTrad/MarketPrice";
import BorderCrossingRegistry  from "../../component/MarketTrad/Bordercrossingregistry";
import VetCertificatesRegistry from "../../component/MarketTrad/Vetcertificateregistry";
import UnconfiguredSubmodulePlaceholder from "../../component/shared/UnconfiguredSubmodulePlaceholder";
import { useAuth }             from "../../context/AuthContext";
import { useModuleTabs }       from "../../config/moduleCatalog";

const LOCAL_TABS = [
  { key: "market-trade",                    label: "Market Registry",                  roles: null          },
  { key: "market-prices",                   label: "Market Prices",                    roles: null          },
  { key: "border-crossing-registry",        label: "Border Crossing Registry",         roles: null          },
  { key: "veterinary-certificate-registry", label: "Veterinary Certificates Registry", roles: null          },
];

// Tabs R05 may access (no border crossing — R05 has no access per RBAC matrix)
const R05_TAB_KEYS = new Set(["market-prices"]);

function canCreateMarketsRecord(roleId, tabKey) {
  switch (roleId) {
    case "R01":
      return true;
    case "R02":
      return false;
    case "R03":
      return tabKey === "border-crossing-registry" || tabKey === "veterinary-certificate-registry";
    case "R04":
      return true;
    case "R05":
      return tabKey === "market-prices";
    case "R06":
    default:
      return false;
  }
}

function TabNav({ tabs, active, onChange }) {
  return (
    <nav className="flex flex-wrap gap-x-7 gap-y-2 border-b border-gray-200">
      {tabs.map(({ key, label, disabled }) => {
        const isActive = active === key;
        return (
          <button
            key={key}
            onClick={() => !disabled && onChange(key)}
            disabled={disabled}
            className={`relative pb-3 text-sm transition-colors ${
              disabled
                ? "font-medium text-gray-400 cursor-not-allowed opacity-50"
                : isActive
                ? "font-semibold text-green-600"
                : "font-medium text-gray-700 hover:text-green-700"
            }`}
          >
            {label}
            {isActive && <span className="absolute inset-x-0 -bottom-px h-0.5 rounded bg-green-600" />}
          </button>
        );
      })}
    </nav>
  );
}

export default function MarketsTrade() {
  const navigate   = useNavigate();
  const { user }   = useAuth();
  const roleId = user?.roleId ?? '';
  const isR05 = roleId === 'R05';
  const tabs = useModuleTabs("markets-trade", LOCAL_TABS);

  // Filter tabs based on role — new (catalog-only) tabs aren't in
  // R05_TAB_KEYS, so R05 never sees them until explicitly granted.
  const visibleTabs = useMemo(
    () => isR05 ? tabs.filter((t) => R05_TAB_KEYS.has(t.key)) : tabs,
    [isR05, tabs]
  );

  const { pathname } = useLocation();
  const segment = pathname.split("/").filter(Boolean).pop();
  const found   = visibleTabs.find((t) => t.key === segment && !t.disabled);
  const activeTab = found ? found.key : visibleTabs.find((t) => !t.disabled)?.key ?? visibleTabs[0]?.key ?? '';
  const activeTabDef = visibleTabs.find((t) => t.key === activeTab);
  const canCreateActiveTab = canCreateMarketsRecord(roleId, activeTab);

  const tabToSubModule = {
    'market-trade': 'registry',
    'market-prices': 'prices',
    'border-crossing-registry': 'border',
    'veterinary-certificate-registry': 'vet',
  };

  const handleTabChange = (key) => navigate(`/markets-trade/${key}`);

  const openCreate = (config = {}) => {
    const subModule = config.subModule ?? tabToSubModule[activeTab] ?? '';
    navigate(`/markets-trade/new?subModule=${subModule}`);
  };

  return (
    <div className="p-6 bg-white min-h-screen">
      <div className="mb-5">
        <TabNav tabs={visibleTabs} active={activeTab} onChange={handleTabChange} />
      </div>

      {activeTabDef?.isCatalogOnly && <UnconfiguredSubmodulePlaceholder label={activeTabDef.label} />}
      {activeTab === "market-trade"                    && <MarketRegistry          onCreateNew={canCreateActiveTab ? openCreate : null} />}
      {activeTab === "market-prices"                   && <MarketPrices            onCreateNew={canCreateActiveTab ? openCreate : null} />}
      {activeTab === "border-crossing-registry"        && <BorderCrossingRegistry  onCreateNew={canCreateActiveTab ? openCreate : null} />}
      {activeTab === "veterinary-certificate-registry" && <VetCertificatesRegistry onCreateNew={canCreateActiveTab ? openCreate : null} />}
    </div>
  );
}
